import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

const STORAGE_KEY = "opened_candidato_ids";

function loadOpenedIds() {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveOpenedIds(set) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch (_) {}
}

const UnopenedCandidatosContext = createContext(null);

export function UnopenedCandidatosProvider({ children }) {
  const [openedIds, setOpenedIds] = useState(loadOpenedIds);
  const [unopenedCandidatosCount, setUnopenedCandidatosCount] = useState(0);
  const openedIdsRef = useRef(openedIds);
  openedIdsRef.current = openedIds;

  useEffect(() => {
    saveOpenedIds(openedIds);
  }, [openedIds]);

  const markCandidatoOpened = useCallback((id) => {
    if (!id) return;
    setOpenedIds((prev) => {
      const next = new Set(prev);
      if (next.has(String(id))) return prev;
      next.add(String(id));
      return next;
    });
    setUnopenedCandidatosCount((c) => Math.max(0, c - 1));
  }, []);

  /** Actualiza el conteo de no abiertos a partir de la lista actual de ids de candidatos (p. ej. tras cargar la lista). */
  const syncUnopenedFromCandidatoIds = useCallback((candidatoIds) => {
    if (!Array.isArray(candidatoIds)) return;
    const opened = openedIdsRef.current;
    const unopened = candidatoIds.filter((id) => !opened.has(String(id))).length;
    setUnopenedCandidatosCount(unopened);
  }, []);

  /** Marca todos los candidatos como abiertos (borra badges de no leídos). candidatoIds: array de ids. */
  const markAllCandidatosOpened = useCallback((candidatoIds) => {
    if (!Array.isArray(candidatoIds)) return;
    setOpenedIds((prev) => {
      const next = new Set(prev);
      candidatoIds.forEach((id) => id != null && next.add(String(id)));
      return next;
    });
    setUnopenedCandidatosCount(0);
  }, []);

  return (
    <UnopenedCandidatosContext.Provider
      value={{
        unopenedCandidatosCount,
        markCandidatoOpened,
        syncUnopenedFromCandidatoIds,
        markAllCandidatosOpened,
      }}
    >
      {children}
    </UnopenedCandidatosContext.Provider>
  );
}

export function useUnopenedCandidatos() {
  const ctx = useContext(UnopenedCandidatosContext);
  return (
    ctx || {
      unopenedCandidatosCount: 0,
      markCandidatoOpened: () => {},
      syncUnopenedFromCandidatoIds: () => {},
      markAllCandidatosOpened: () => {},
    }
  );
}
